import { useState, useEffect, useRef } from "react";
import api, { eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { Loader2, AlertTriangle, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import FunnelButton from "@/components/FunnelButton";

const fmtDate = (d) => { if (!d) return "—"; const s = String(d).slice(0, 10); return s.split("-").reverse().join("/"); };
const qty = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 }));

const DECIS = {
  da_decidere: { label: "Da decidere", cls: "bg-slate-500 text-white" },
  includi: { label: "Nei movimenti", cls: "bg-emerald-600 text-white" },
  escludi: { label: "Escluso", cls: "bg-zinc-600 text-white" },
};

const EVAS = {
  evaso: { label: "Evaso", cls: "bg-emerald-600 text-white" },
  parziale: { label: "Parziale", cls: "bg-amber-500 text-white" },
  da_evadere: { label: "Da evadere", cls: "bg-rose-600 text-white" },
};

function DettaglioDialog({ num, canDecide, onSaved, onClose }) {
  const [d, setD] = useState(null);
  const [dataPrev, setDataPrev] = useState("");
  const [busy, setBusy] = useState(false);
  const [verBusy, setVerBusy] = useState(false);
  const load = () => api.get(`/commesse/orfane/dettaglio?num=${encodeURIComponent(num)}`)
    .then((r) => { setD(r.data); setDataPrev(r.data.data_prevista || ""); })
    .catch(() => setD(false));
  useState(() => { load(); return undefined; });

  const verifica = async () => {
    setVerBusy(true);
    try {
      const r = await api.post("/commesse/orfane/verifica-evasione", { num });
      toast.success(`Evasione verificata su ${r.data.commesse_n} commesse · ${r.data.evasi} evasi, ${r.data.parziali} parziali, ${r.data.da_evadere} da evadere`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verifica non riuscita");
    } finally { setVerBusy(false); }
  };

  const decide = async (decisione) => {
    if (decisione === "includi" && !dataPrev) { toast.error("Seleziona la data prevista"); return; }
    setBusy(true);
    try {
      const r = await api.post("/commesse/orfane/decisione", { num, decisione, data_prevista: dataPrev });
      toast.success(decisione === "includi" ? `Incluso nei movimenti (${r.data.articoli_aggiornati} articoli)` : decisione === "escludi" ? "Ordine escluso" : "Riportato a: da decidere");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operazione non riuscita");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="orfane-dialog">
        <DialogHeader><DialogTitle>Ordine {num} · dettaglio articoli</DialogTitle></DialogHeader>
        {d === null ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
        ) : d === false ? (
          <div className="py-6 text-sm text-destructive">Impossibile caricare il dettaglio.</div>
        ) : (
          <div className="space-y-3">
            {d.cliente && d.cliente.name && (
              <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="orfane-cliente">
                <div className="text-sm font-bold text-foreground">{d.cliente.name}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                  {d.cliente.piva && <span>P.IVA {d.cliente.piva}</span>}
                  {(d.cliente.address || d.cliente.city) && <span>{d.cliente.address || [d.cliente.city, d.cliente.provincia].filter(Boolean).join(" ")}</span>}
                  {d.cliente.phone && <span>{d.cliente.phone}</span>}
                  {d.cliente.email && <span>{d.cliente.email}</span>}
                  {d.cliente.referente && <span>Rif. {d.cliente.referente}</span>}
                  {d.cliente.agenzia && <span>Agenzia: {d.cliente.agenzia}</span>}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span>Stato: <b>{d.stato || "—"}</b></span>
              <span>Data ordine: <b>{fmtDate(d.date)}</b></span>
              <span>Importo: <b>{eur(d.importo)}</b></span>
              <span>kWp: <b>{qty(d.kwp)}</b></span>
              <span>Pagamento: <b>{d.modalita_pagamento || "—"}</b></span>
              <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={verifica} disabled={verBusy} data-testid="orfane-verifica-evasione">
                {verBusy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}Verifica evasione
              </Button>
            </div>
            {d.evasione_verificata && (
              <div className="text-[11px] text-muted-foreground" data-testid="orfane-evasione-info">
                Evaso verificato su {d.evasione_commesse_n ?? "—"} commesse collegate · aggiornato il {d.evasione_verificata_al ? new Date(d.evasione_verificata_al).toLocaleString("it-IT") : "—"}
              </div>
            )}
            <div className="max-h-[45vh] overflow-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/70 text-left">
                  <tr>
                    <th className="px-2 py-1 font-semibold">Codice</th>
                    <th className="px-2 py-1 font-semibold">Nome</th>
                    <th className="px-2 py-1 text-right font-semibold">Q.tà</th>
                    <th className="px-2 py-1 text-center font-semibold">Stato</th>
                    <th className="px-2 py-1 text-right font-semibold">Da impegnare</th>
                    <th className="px-2 py-1 text-right font-semibold">Giacenza</th>
                    <th className="px-2 py-1 text-right font-semibold">Disp. ora</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.righe || []).map((r, i) => {
                    const ev = EVAS[r.stato_evasione];
                    return (
                    <tr key={i} className="border-t border-border" data-testid="orfane-riga">
                      <td className="px-2 py-1 font-mono text-muted-foreground">{r.codice}{!r.a_listino && <Badge variant="secondary" className="ml-1 text-[9px]">fuori listino</Badge>}</td>
                      <td className="px-2 py-1">{r.nome}</td>
                      <td className="px-2 py-1 text-right font-mono">{qty(r.quantita)}</td>
                      <td className="px-2 py-1 text-center">
                        {ev ? (
                          <Badge className={`${ev.cls} text-[9px]`} data-testid="orfane-stato-evasione">{ev.label}{r.stato_evasione === "parziale" && r.gia_consegnata != null ? ` (${qty(r.gia_consegnata)})` : ""}</Badge>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-1 text-right font-mono font-semibold text-amber-600 dark:text-amber-400">{r.a_listino ? qty(r.da_impegnare) : "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.giacenza == null ? "—" : qty(r.giacenza)}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.disponibile_ora == null ? "—" : qty(r.disponibile_ora)}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-muted/20 p-3">
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Data prevista (impegno)</div>
                <div className="w-48"><DatePicker value={dataPrev} onChange={setDataPrev} data-testid="orfane-data-prevista" /></div>
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                {canDecide ? (
                  <>
                    <Button size="sm" onClick={() => decide("includi")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="orfane-includi">
                      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Includi nei movimenti
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => decide("escludi")} disabled={busy} data-testid="orfane-escludi">Escludi</Button>
                    <Button size="sm" variant="outline" onClick={() => decide("da_decidere")} disabled={busy} data-testid="orfane-reset">Da decidere</Button>
                  </>
                ) : <span className="text-xs text-muted-foreground">Decisione riservata ad Admin / Back Office</span>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NotaCell({ num, initial, canEdit }) {
  const [v, setV] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post("/commesse/nota", { ordine: num, nota: v }); toast.success("Nota salvata"); }
    catch { toast.error("Nota non salvata"); }
    finally { setSaving(false); }
  };
  if (!canEdit) return <span className="text-muted-foreground">{v || "—"}</span>;
  return (
    <input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} disabled={saving}
      placeholder="Nota…" className="w-36 rounded border border-border bg-background px-1.5 py-0.5 text-[11px]"
      data-testid={`orfane-nota-${num}`} />
  );
}

const EVAS_ORD = {
  evaso: { label: "Evaso", cls: "bg-emerald-600 text-white" },
  parziale: { label: "Parziale", cls: "bg-amber-500 text-white" },
  da_evadere: { label: "Da evadere", cls: "bg-rose-600 text-white" },
  sconosciuto: { label: "N/D", cls: "bg-zinc-500 text-white" },
};

export default function OrdiniSenzaCommessa({ canDecide }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [ver, setVer] = useState(null);
  const [funnels, setFunnels] = useState({});
  const [funnelTipi, setFunnelTipi] = useState({});
  const loadFunnels = () => api.get("/commesse/funnel").then((r) => { setFunnels(r.data.funnels || {}); setFunnelTipi(r.data.tipi || {}); }).catch(() => {});
  const pollRef = useRef(null);
  const startTsRef = useRef(0);
  const sawRunningRef = useRef(false);
  const load = () => { loadFunnels(); return api.get("/commesse/orfane").then((r) => setData(r.data)).catch(() => setData(false)); };

  const poll = () => {
    api.get("/commesse/orfane/verifica-tutti/stato").then((r) => {
      const s = r.data || {};
      setVer(s);
      if (s.running) {
        sawRunningRef.current = true;
        load(); // aggiorna i badge man mano che il task procede
        pollRef.current = setTimeout(poll, 2500);
        return;
      }
      // Non in esecuzione: distinguo una FINE reale dal caso "non ancora partito".
      const finishedTs = s.finished_at ? new Date(s.finished_at).getTime() : 0;
      const finishedOurs = finishedTs && finishedTs >= startTsRef.current - 5000;
      const elapsed = Date.now() - startTsRef.current;
      if (sawRunningRef.current || finishedOurs) {
        if (s.error) toast.error("Verifica interrotta: " + s.error);
        else if ((s.total || 0) === 0) toast.info(s.message || "Nessun ordine da verificare.");
        else toast.success(`Verifica completata · ${s.evasi} evasi (esclusi), ${s.parziali} parziali, ${s.da_evadere} da evadere`);
        sawRunningRef.current = false;
        load();
        return;
      }
      if (elapsed < 20000) { // attendo che lo stato del task compaia (evita falsi "completata 0/0/0")
        pollRef.current = setTimeout(poll, 1500);
      }
    }).catch(() => { pollRef.current = setTimeout(poll, 3000); });
  };

  const startVerifica = async () => {
    try {
      await api.post("/commesse/orfane/verifica-tutti", {});
      startTsRef.current = Date.now();
      sawRunningRef.current = false;
      setVer({ running: true, done: 0, total: 0, message: "Avvio in corso… la verifica lavora in background." });
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(poll, 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile avviare la verifica");
    }
  };

  // All'apertura: se una verifica è già in corso (avviata prima o su altro dispositivo), riprendo il monitoraggio.
  const resumeIfRunning = () => {
    api.get("/commesse/orfane/verifica-tutti/stato").then((r) => {
      const s = r.data || {};
      setVer(s);
      if (s.running) {
        sawRunningRef.current = true;
        startTsRef.current = s.started_at ? new Date(s.started_at).getTime() : Date.now();
        clearTimeout(pollRef.current);
        pollRef.current = setTimeout(poll, 1500);
      }
    }).catch(() => {});
  };

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const sem = ver && ver.running
    ? { c: "bg-amber-400 animate-pulse", t: "Verifica in corso in background" }
    : ver && ver.error
    ? { c: "bg-rose-500", t: "Ultima verifica in errore" }
    : ver && ver.finished_at && !ver.never_run
    ? { c: "bg-emerald-500", t: `Ultima verifica: ${new Date(ver.finished_at).toLocaleString("it-IT")}` }
    : { c: "bg-zinc-400", t: "Verifica mai eseguita" };

  return (
    <details className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground" data-testid="commesse-orfane"
      onToggle={(e) => { if (e.target.open && data === null) { load(); resumeIfRunning(); } }}>
      <summary className="flex cursor-pointer items-center gap-2 font-semibold text-foreground">
        <AlertTriangle className="h-3.5 w-3.5 text-primary" />Ordini aperti senza commessa (decidi cosa conta nei movimenti)
      </summary>
      {data === null ? (
        <div className="mt-2 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Calcolo…</div>
      ) : data === false ? (
        <div className="mt-2 text-destructive">Impossibile calcolare.</div>
      ) : (
        <div className="mt-2 space-y-2" data-testid="commesse-orfane-body">
          {canDecide && (
            <div className="flex flex-col gap-2 rounded-md border border-primary/40 bg-primary/5 p-3 sm:flex-row sm:items-center" data-testid="orfane-verifica-box">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground"><ScanSearch className="h-4 w-4 text-primary" />Verifica evasione (intelligente)</div>
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                  Controlla nel gestionale le commesse collegate a ciascun ordine e attribuisce lo stato reale ad ogni articolo.
                  Gli ordini <b className="text-emerald-600 dark:text-emerald-400">totalmente evasi</b> vengono esclusi automaticamente dai movimenti,
                  quelli <b className="text-amber-600 dark:text-amber-400">parzialmente evasi</b> vengono evidenziati, quelli <b>non evasi</b> restano da decidere.
                  Analizza solo gli ordini ancora «Da decidere» e non riscarica quelli già verificati.
                </p>
                {ver && ver.running && (
                  <div className="mt-2 rounded-md border border-primary/30 bg-background/60 p-2" data-testid="orfane-verifica-progress">
                    <div className="flex items-center gap-2 text-[11px] font-medium text-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                      Verifica in corso in background… {ver.total ? `${ver.done}/${ver.total}` : ""}
                    </div>
                    {ver.total > 0 && (
                      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(100, Math.round((ver.done / ver.total) * 100))}%` }} />
                      </div>
                    )}
                    <div className="mt-1 text-[10px] text-muted-foreground">{ver.message || "…"}</div>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-[10px]">
                      <span className="text-emerald-600 dark:text-emerald-400">Evasi {ver.evasi || 0}</span>
                      <span className="text-amber-600 dark:text-amber-400">Parziali {ver.parziali || 0}</span>
                      <span className="text-rose-600 dark:text-rose-400">Da evadere {ver.da_evadere || 0}</span>
                      <span>N/D {ver.sconosciuti || 0}</span>
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground">Puoi continuare a lavorare: i risultati compaiono man mano. La verifica prosegue anche se chiudi questo pannello.</div>
                  </div>
                )}
                {ver && !ver.running && ver.finished_at && !ver.never_run && (
                  <div className="mt-2 text-[11px] text-muted-foreground" data-testid="orfane-verifica-done">
                    Ultima verifica: <b className="text-emerald-600 dark:text-emerald-400">{ver.evasi} evasi</b> (esclusi) · <b className="text-amber-600 dark:text-amber-400">{ver.parziali} parziali</b> · {ver.da_evadere} da evadere · {ver.sconosciuti || 0} senza commesse collegate · {ver.scraped || 0} letti dal gestionale, {ver.cached || 0} da cache · {new Date(ver.finished_at).toLocaleString("it-IT")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${sem.c}`} title={sem.t} data-testid="orfane-verifica-semaforo" />
                <Button size="sm" onClick={startVerifica} disabled={ver && ver.running} className="bg-primary hover:bg-primary/90" data-testid="orfane-verifica-tutti-btn">
                  {ver && ver.running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanSearch className="mr-2 h-4 w-4" />}{ver && ver.running ? "In corso…" : "Avvia verifica"}
                </Button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-4">
            <span>Ordini aperti: <b className="text-foreground">{data.co_aperti}</b></span>
            <span>Con commessa: <b className="text-emerald-600 dark:text-emerald-400">{data.co_con_commessa}</b></span>
            <span>Senza commessa: <b className="text-amber-600 dark:text-amber-400">{data.orfane_totali}</b></span>
            <span>Da decidere: <b className="text-foreground">{data.da_decidere}</b></span>
            <span className="text-muted-foreground">(esclusi {data.co_chiusi_esclusi} ordini «Chiuso»)</span>
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-[11px]">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="px-2 py-1 font-semibold">Ordine</th>
                  <th className="px-2 py-1 font-semibold">Cliente</th>
                  <th className="px-2 py-1 text-right font-semibold">Importo</th>
                  <th className="px-2 py-1 font-semibold">Data</th>
                  <th className="px-2 py-1 font-semibold">Stato</th>
                  <th className="px-2 py-1 font-semibold">Evasione</th>
                  <th className="px-2 py-1 font-semibold">Decisione</th>
                  <th className="px-2 py-1 font-semibold">Funnel</th>
                  <th className="px-2 py-1 font-semibold">Nota</th>
                  <th className="px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {(data.ordini || []).map((o) => {
                  const dc = DECIS[o.decisione] || DECIS.da_decidere;
                  const ev = EVAS_ORD[o.evasione_stato];
                  const rowCls = o.evasione_stato === "parziale" ? "bg-amber-500/10" : (o.auto_evaso ? "opacity-60" : "");
                  return (
                    <tr key={o.numero_ordine} className={`border-t border-border ${rowCls}`} data-testid="orfane-ordine-row">
                      <td className="px-2 py-1 font-mono font-semibold">{o.numero_ordine}</td>
                      <td className="px-2 py-1">{o.cliente}</td>
                      <td className="px-2 py-1 text-right font-mono">{eur(o.importo)}</td>
                      <td className="px-2 py-1 font-mono">{fmtDate(o.date)}</td>
                      <td className="px-2 py-1">{o.stato || "—"}</td>
                      <td className="px-2 py-1">{ev ? <Badge className={`${ev.cls} text-[10px]`} data-testid="orfane-row-evasione">{ev.label}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-2 py-1">
                        <button type="button" onClick={() => setSel(o.numero_ordine)} className="inline-flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50" data-testid={`orfane-decidi-${o.numero_ordine}`} title="Apri per decidere">
                          <Badge className={`${dc.cls} cursor-pointer text-[10px]`}>{dc.label}</Badge>
                        </button>
                        {o.auto_evaso && <span className="ml-1 text-[9px] text-emerald-600 dark:text-emerald-400">auto</span>}{o.decisione === "includi" && o.data_prevista && <span className="ml-1 text-[10px] text-muted-foreground">{fmtDate(o.data_prevista)}</span>}
                      </td>
                      <td className="px-2 py-1"><FunnelButton numero={o.numero_ordine} tipi={funnelTipi} funnel={funnels[o.numero_ordine]} canEdit={canDecide} onSaved={loadFunnels} /></td>
                      <td className="px-2 py-1"><NotaCell num={o.numero_ordine} initial={o.nota} canEdit={canDecide} /></td>
                      <td className="px-2 py-1 text-right">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[10px]" onClick={() => setSel(o.numero_ordine)} data-testid={`orfane-dettagli-${o.numero_ordine}`}>Dettagli</Button>
                      </td>
                    </tr>
                  );
                })}
                {(data.ordini || []).length === 0 && (
                  <tr><td colSpan={10} className="px-2 py-3 text-center text-muted-foreground">Nessun ordine aperto senza commessa.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[10px]">«Includi nei movimenti» aggiunge subito gli articoli dell'ordine agli impegni di magazzino (con la data prevista che scegli). Elenco commesse aggiornato il {data.elenco_sincronizzato_al ? new Date(data.elenco_sincronizzato_al).toLocaleString("it-IT") : "—"}.</p>
        </div>
      )}
      {sel && <DettaglioDialog num={sel} canDecide={canDecide} onSaved={load} onClose={() => setSel(null)} />}
    </details>
  );
}
